/**
 * ============================================================================
 * Biography — derived from the content layer (single source of truth).
 * ============================================================================
 *
 * Real data lives in `src/content/data/profile.json` and flows through the
 * typed loader (`src/content`). This file only adapts the canonical `Profile`
 * shape into the flat `Biography` shape that HeroSection, Navbar, and Footer
 * already read. To change what is displayed, edit the JSON — not this file and
 * not the components.
 *
 * Transitional by design (Constitution V.4; precedent `constants/experience.ts`,
 * D-006). New components consume the loader directly.
 *
 * THERE IS NO STARTER-TEMPLATE DATA LEFT IN THIS FILE. `experiences` and
 * `projects` were the last two blocks, and they went with the v1 Experience and
 * v1 Projects components on 2026-08-19 (D-064) — the final entries on Article
 * V.6's Phase 1 list.
 *
 * `projects` is worth a line on the way out. It carried two other people's
 * REAL LinkedIn profiles, GitHub handles and avatar URLs — the template
 * author's and a collaborator's. None of it rendered, because the component
 * was commented out of `Home.tsx`, but `sourcesContent` in the deployed
 * sourcemap carried the whole file, so the URLs were publicly fetchable from
 * sourishmukherjee.com. Not a Confidentiality Law matter (Article VI is about
 * CLIENT names) and not an incident — but third-party personal data shipping
 * from a documentary about someone else is its own kind of wrong, and it is
 * the reason this deletion was pulled forward rather than left on the list.
 *
 * The earlier departures, each when its last consumer stopped needing it:
 * 2026-08-13 `educations` with v1 Education, `educations_v2` when Education_v2
 * was rewritten on the content layer, and `skills` — the 29-entry template icon
 * list Constitution V.6 names by name — when Skills was. 2026-08-15
 * `TimeLineData`, which had no consumer to lose (D-046).
 */

import { profile, socialUrl } from "../content";

export interface Biography {
	name: string;
	/** Typewriter strings in the hero. */
	roles: string[];
	description: string;
	linkedin: string;
}

/**
 * The summary's opening `count` sentences, whole — never a mid-sentence cut.
 * Falls back to the full text if the sentence split finds nothing.
 */
const leadSentences = (text: string, count: number): string =>
	(text.match(/[^.]+\.(?:\s+|$)/g) ?? [text]).slice(0, count).join("").trim();

export const BIOGRAPHY: Biography = {
	name: profile.name,

	// Only the headline's first segment is a job title; the segments after it
	// are domains ("Enterprise SaaS & Digital Transformation"), which read as
	// broken English after the hero's fixed "I am a" and blow out the
	// flex row they are typed into.
	roles: profile.headline.split(" | ").slice(0, 1),

	// The full summary runs twelve lines at desktop and pushes the hero's call
	// to action below the fold, so the hero carries its first two sentences.
	// The complete text stays canonical in profile.json for the narrative
	// sections that Phase 2 adds.
	description: leadSentences(profile.summary, 2),

	// Read by the Navbar. The Footer no longer comes through this bridge — it
	// consumes the loader directly (Constitution V.4), which is where the
	// `facebook` and `insta` literals used to go. Both were dropped rather than
	// promoted into profile.json: personal social accounts are not evidence,
	// and this is a documentary (Article I).
	linkedin: socialUrl("linkedin")
};
